/**
 * Data Lineage Utility
 * Helper functions to log data changes across the application
 */

/**
 * Log a data change to the lineage system
 * @param {Object} params - Change parameters
 * @param {string} params.entityType - Type of entity (user, role, persona, question)
 * @param {string} params.entityId - ID of the entity
 * @param {string} params.entityName - Human-readable name
 * @param {string} params.fieldName - Field that changed
 * @param {string} params.oldValue - Previous value
 * @param {string} params.newValue - New value
 * @param {string} params.changeType - Type of change (CREATE, UPDATE, DELETE)
 * @param {string} params.changedBy - User who made the change (default: 'Admin')
 * @param {string} params.changeReason - Optional reason for change
 */
export const logDataChange = async ({
  entityType,
  entityId,
  entityName,
  fieldName,
  oldValue,
  newValue,
  changeType,
  changedBy = 'Admin',
  changeReason = null
}) => {
  try {
    // Convert null/undefined to string representation
    const formatValue = (val) => {
      if (val === null || val === undefined) return null;
      return String(val);
    };

    const response = await fetch('/api/data_lineage/log_change', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        entity_name: entityName,
        field_name: fieldName,
        old_value: formatValue(oldValue),
        new_value: formatValue(newValue),
        change_type: changeType,
        changed_by: changedBy,
        change_reason: changeReason
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to log data change:', response.status, errorText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error logging data change:', error);
    return false;
  }
};

/**
 * Log multiple field changes for a single entity
 * @param {Object} params - Base parameters
 * @param {Array} changes - Array of {fieldName, oldValue, newValue}
 */
export const logMultipleChanges = async (baseParams, changes) => {
  const promises = changes.map(change =>
    logDataChange({
      ...baseParams,
      fieldName: change.fieldName,
      oldValue: change.oldValue,
      newValue: change.newValue
    })
  );

  try {
    await Promise.all(promises);
  } catch (error) {
    console.error('Error logging multiple changes:', error);
  }
};

/**
 * Log user creation
 */
export const logUserCreate = (userId, userName, userData) => {
  return logDataChange({
    entityType: 'user',
    entityId: userId,
    entityName: userName,
    fieldName: 'user_created',
    oldValue: null,
    newValue: JSON.stringify(userData),
    changeType: 'CREATE'
  });
};

/**
 * Log user update
 */
export const logUserUpdate = (userId, userName, fieldName, oldValue, newValue) => {
  return logDataChange({
    entityType: 'user',
    entityId: userId,
    entityName: userName,
    fieldName,
    oldValue,
    newValue,
    changeType: 'UPDATE'
  });
};

/**
 * Log user deletion
 */
export const logUserDelete = (userId, userName) => {
  return logDataChange({
    entityType: 'user',
    entityId: userId,
    entityName: userName,
    fieldName: 'user_deleted',
    oldValue: userName,
    newValue: null,
    changeType: 'DELETE'
  });
};

/**
 * Log role changes
 */
export const logRoleChange = (roleId, roleName, changeType, fieldName, oldValue, newValue) => {
  return logDataChange({
    entityType: 'role',
    entityId: roleId,
    entityName: roleName,
    fieldName,
    oldValue,
    newValue,
    changeType
  });
};

/**
 * Log persona changes
 */
export const logPersonaChange = (personaId, personaName, changeType, fieldName, oldValue, newValue) => {
  return logDataChange({
    entityType: 'persona',
    entityId: personaId,
    entityName: personaName,
    fieldName,
    oldValue,
    newValue,
    changeType
  });
};

/**
 * Log question changes
 */
export const logQuestionChange = (questionId, questionText, changeType, fieldName, oldValue, newValue) => {
  return logDataChange({
    entityType: 'question',
    entityId: questionId.toString(),
    entityName: questionText.substring(0, 50) + '...',
    fieldName,
    oldValue,
    newValue,
    changeType
  });
};

